import { Chip, IconButton, Tooltip } from "@material-ui/core";
import CreateIcon from "@material-ui/icons/Create";
import InfoIcon from "@material-ui/icons/Info";
import { useTranslations } from "adapters/i18n";
import type { CustomTableColumn } from "features/shared/components";
import SwitchCustomizado from "features/shared/components/SwitchCustomizado";
import React, { useMemo } from "react";
import numberUtils from "shared/numberUtils";
import { EnrichedAnimalCategory } from "shared/types";
import type { CategoryFieldsVisibility } from "../category.types";
import {
  CategoryListTableColumn,
  RED_INFO_ICON_PATH,
} from "../utils/category.utils";

type UseCategoryListTableProps = {
  classNames: { yesChip: string; noChip: string };
  fieldsVisibility: CategoryFieldsVisibility;
  isCategoryFormPanelOpen: boolean;
  onEditClick: (row: EnrichedAnimalCategory) => void;
  onToggleCategoryStatus: (
    row: EnrichedAnimalCategory,
    isCurrentDeleted: boolean
  ) => void;
};

export function useCategoryListTable({
  classNames,
  fieldsVisibility,
  isCategoryFormPanelOpen,
  onEditClick,
  onToggleCategoryStatus,
}: UseCategoryListTableProps) {
  const { t } = useTranslations();

  const columns = useMemo<
    CustomTableColumn<EnrichedAnimalCategory, typeof CategoryListTableColumn>[]
  >(
    () => [
      {
        id: CategoryListTableColumn.Name,
        label: t("animalCategory:categories"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => row.name,
        renderCell: (_, row) => <NameColumnCell row={row} />,
      },
      {
        id: CategoryListTableColumn.AmountForConciliation,
        label: t("conciliation:initial-amount-for-conciliation"),
        hide: !fieldsVisibility.showAmountForConciliation,
        isSortable: true,
        type: "number",
        getRowValue: (row) =>
          numberUtils.parseValidNonZero(row.initialNumberConference),
        renderCell: (value) => (value != null ? value.toString() : "-"),
      },
      {
        id: CategoryListTableColumn.LotSize,
        label: t("animalCategory:desired-herd-size"),
        hide: !fieldsVisibility.showHeadCount,
        isSortable: true,
        type: "number",
        getRowValue: (row) => numberUtils.parseValidNonZero(row.desiredNumber),
        renderCell: (value) => (value != null ? value.toString() : "-"),
        trailingHeaderElement: (
          <Tooltip
            data-testid="lot-size-info-tooltip"
            title={t("animalCategory:size-field-description")}
            style={{ maxWidth: 60 }}
          >
            <InfoIcon color="primary" />
          </Tooltip>
        ),
      },
      {
        id: CategoryListTableColumn.Gender,
        label: t("common:gender"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => row.gender,
      },
      {
        id: CategoryListTableColumn.AgeRange,
        label: t("animalCategory:age-range"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => row.ageRange?.ageRange ?? "",
      },
      {
        id: CategoryListTableColumn.WeightGainCurve,
        label: t("livestock:weight-gain-curve"),
        hide: !fieldsVisibility.showWeightGainCurve,
        isSortable: true,
        type: "boolean",
        getRowValue: (row) => row.weightGainCurveId != null,
        renderCell: (value) => (
          <WeightGainCurveCell
            classNames={classNames}
            hasWeightGainCurve={value}
          />
        ),
      },
      {
        id: CategoryListTableColumn.Edit,
        label: t("common:actions.edit"),
        isActionColumn: true,
        isSortable: false,
        type: "custom",
        renderCell: (row) => (
          <EditCell
            isCategoryFormPanelOpen={isCategoryFormPanelOpen}
            onEditClick={onEditClick}
            row={row}
          />
        ),
      },
      {
        id: CategoryListTableColumn.Status,
        label: t("common:status.active"),
        isActionColumn: true,
        isSortable: false,
        type: "custom",
        renderCell: (row) => (
          <StatusCell
            onToggleCategoryStatus={onToggleCategoryStatus}
            row={row}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      classNames,
      fieldsVisibility.showAmountForConciliation,
      fieldsVisibility.showHeadCount,
      fieldsVisibility.showWeightGainCurve,
      isCategoryFormPanelOpen,
      t,
    ]
  );

  return { columns };
}

type NameColumnCellProps = {
  row: EnrichedAnimalCategory;
};

function NameColumnCell({ row }: NameColumnCellProps) {
  const { t } = useTranslations();

  const showTooltip =
    row.warnings != null && row.deletedAt == null && row.deletionUser == null;

  return (
    <>
      {showTooltip && (
        <Tooltip title={row.warnings} placement="right">
          <IconButton
            aria-label={t("animalCategory:category-has-warnings")}
            data-testid="warning-tooltip-trigger"
          >
            <img src={RED_INFO_ICON_PATH} alt={t("common:info-icon")} />
          </IconButton>
        </Tooltip>
      )}
      <span>{row.name}</span>
    </>
  );
}

type WeightGainCurveCellProps = {
  classNames: { yesChip: string; noChip: string };
  hasWeightGainCurve: boolean | null;
};

function WeightGainCurveCell({
  classNames,
  hasWeightGainCurve = false,
}: WeightGainCurveCellProps) {
  const { t } = useTranslations();

  const label = hasWeightGainCurve
    ? t("common:actions.yes")
    : t("common:actions.no");

  const className = hasWeightGainCurve ? classNames.yesChip : classNames.noChip;

  return (
    <Chip
      className={className}
      data-testid="weight-gain-curve-chip"
      label={label}
    />
  );
}

type EditCellProps = {
  isCategoryFormPanelOpen: boolean;
  onEditClick: (row: EnrichedAnimalCategory) => void;
  row: EnrichedAnimalCategory;
};

function EditCell({
  isCategoryFormPanelOpen,
  onEditClick,
  row,
}: EditCellProps) {
  const { t } = useTranslations();

  const handleEditClick = () => {
    onEditClick(row);
  };

  return (
    <IconButton
      aria-label={t("common:actions.edit")}
      data-testid="edit-category-button"
      disabled={isCategoryFormPanelOpen}
      onClick={handleEditClick}
    >
      <CreateIcon />
    </IconButton>
  );
}

type StatusCellProps = {
  onToggleCategoryStatus: (
    row: EnrichedAnimalCategory,
    isCurrentDeleted: boolean
  ) => void;
  row: EnrichedAnimalCategory;
};

function StatusCell({ onToggleCategoryStatus, row }: StatusCellProps) {
  const isDeleted = row.deletedAt != null || row.deletionUser != null;

  const handleToggle = () => {
    onToggleCategoryStatus(row, isDeleted);
  };

  return (
    <SwitchCustomizado defaultChecked={!isDeleted} handleClick={handleToggle} />
  );
}
